import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ILocale } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { actionProcessing } from "infrastructure/store";
import { getScreenExtraReducers } from "ui/app";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { IIndustry } from "ui/shared/models/common";

import {
  ITranslationsManagerState,
  getInitialTranslationsManagerState,
  ITranslationsManagerFilter,
  ITranslationsManagerFilterData,
  ITermTranslationRow,
  IChangeText,
  ITermTranslationModifyData
} from "./TranslationsManager.state";

const initialState = getInitialTranslationsManagerState();

export const translationsManager = createSlice({
  name: "translationsManager",
  initialState: initialState,
  reducers: {
    initialize: state => {
      return { ...state, isLoading: true };
    },
    loadingFailed: (state, action: PayloadAction<INotification>) => {
      state.isLoading = false;
      state.filter.industryCanOpen = false;
      state.filter.localeCanOpen = false;
      state.notifications.push(action.payload);
      return state;
    },
    loadIndustries: (state, _: PayloadAction<IPickerSearchCriterion<IIndustry>>) => {
      return { ...state, isLoading: true, filter: { ...state.filter, industryCanOpen: undefined } };
    },
    initializeCompleted: (state, action: PayloadAction<ITranslationsManagerFilter>) => {
      return { ...state, isLoading: false, filter: action.payload };
    },
    applyFilter: (state: ITranslationsManagerState, action: PayloadAction<ITranslationsManagerFilterData>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, lastData: action.payload } };
    },
    search: actionProcessing.createWithClearNotification((state: ITranslationsManagerState) => {
      return { ...state, isLoading: true };
    }),
    searchCompleted: (state, action: PayloadAction<ITermTranslationRow[]>) => {
      return {
        ...state,
        isLoading: false,
        grid: { ...state.grid, gridData: action.payload, selected: [], isSearched: true },
        isExportDisabled: action.payload.length === 0
      };
    },
    selectTermTranslations: (state: ITranslationsManagerState, action: PayloadAction<ITermTranslationRow[]>) => {
      return {
        ...state,
        grid: { ...state.grid, selected: action.payload }
      };
    },
    approveTermTranslations: actionProcessing.createWithClearNotification((state: ITranslationsManagerState) => {
      return { ...state, isLoading: true };
    }),
    approveTermTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },
    obsoleteTermTranslations: state => {
      return state;
    },
    obsoleteTermTranslationsCompleted: state => {
      return { ...state, grid: { ...state.grid, selected: [] } };
    },
    saveTermTranslations: actionProcessing.createWithClearNotification((state: ITranslationsManagerState) => {
      return { ...state, isLoading: true };
    }),
    saveTermTranslationsCompleted: (state, { payload }: PayloadAction<{ message: INotification; isLoading?: boolean }>) => {
      if (payload.isLoading !== undefined) {
        state.isLoading = payload.isLoading;
      }
      state.notifications.push(payload.message);
      return state;
    },
    loadIndustriesCompleted: (state, action: PayloadAction<IIndustry[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, industries: action.payload } };
    },
    loadLocales: (state, _: PayloadAction<IPickerSearchCriterion<ILocale>>) => {
      return { ...state, isLoading: true, filter: { ...state.filter, localeCanOpen: undefined } };
    },
    loadLocalesCompleted: (state, action: PayloadAction<ILocale[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, locales: action.payload } };
    },
    loadSupportedLocales: state => {
      return {
        ...state,
        filter: { ...state.filter, supportedLocales: undefined }
      };
    },
    loadSupportedLocalesCompleted: (state, action: PayloadAction<ILocale[]>) => {
      return { ...state, filter: { ...state.filter, supportedLocales: action.payload } };
    },
    loadSupportedLocalesFailed: (state, action: PayloadAction<INotification>) => {
      return { ...state, filter: { ...state.filter, supportedLocales: [] }, notifications: [...state.notifications, action.payload] };
    },
    changeTranslation: (state, _action: PayloadAction<IChangeText>) => {
      return state;
    },
    changeTranslationDefinition: (state, _action: PayloadAction<IChangeText>) => {
      return state;
    },
    applyTermTranslationChanges: (state, { payload }: PayloadAction<ITermTranslationModifyData>) => {
      const gridData = [...state.grid.gridData];
      const row = gridData[payload.index];

      gridData[payload.index] = {
        ...row,
        originalData: payload.originalData,
        isTranslationChanged: payload.isTranslationChanged,
        isTranslationDefinitionChanged: payload.isTranslationDefinitionChanged,
        validationIssues: payload.validationIssues,
        data: {
          ...row.data,
          translation: payload.translation,
          translationDefinition: payload.translationDefinition,
          translationId: payload.translationId
        }
      };

      const selected: ITermTranslationRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },
    applyTermTranslationsChanges: (state, { payload }: PayloadAction<ITermTranslationModifyData[]>) => {
      const gridData = [...state.grid.gridData];

      payload.forEach(data => {
        const row = gridData[data.index];

        gridData[data.index] = {
          ...row,
          originalData: data.originalData,
          isTranslationChanged: data.isTranslationChanged,
          isTranslationDefinitionChanged: data.isTranslationDefinitionChanged,
          validationIssues: data.validationIssues,
          data: {
            ...row.data,
            translation: data.translation,
            translationDefinition: data.translationDefinition,
            translationId: data.translationId
          }
        };
      });

      const selected: ITermTranslationRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },
    applyTermTranslationChangesWarning: (state, action: PayloadAction<INotification | undefined>) => {
      if (action.payload) {
        return { ...state, notifications: [action.payload] };
      } else {
        return { ...state, notifications: [] };
      }
    },
    onTermTranslationUploadCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload] };
    },
    exportTranslationsToXml: (state: ITranslationsManagerState) => {
      return state;
    },
    downloadTermTranslationsUploadTemplate: (state: ITranslationsManagerState) => {
      return { ...state, isLoading: true };
    },
    downloadTermTranslationsUploadTemplateCompleted: (state: ITranslationsManagerState) => {
      return { ...state, isLoading: false };
    },
    downloadTermTranslationsUploadTemplateFailed: (state: ITranslationsManagerState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    }
  },
  extraReducers: getScreenExtraReducers<ITranslationsManagerState>("notifications", initialState)
});
